import { useMemo } from "react";

import moment from "moment";

import { SessionPageContextProvider, type BuildHeatSessionGlobalInput } from "heat-next";

import { useSessionDetailsContext } from "@/context/session-details/session-details";

import LayoutGrid from "@/components/layout-grid/layout-grid";
import { VRIntlProviderComponent } from "@/components/providers/intl-provider";
import ErrorBoundary from "@/components/ui/error-boundary";
import Page, { PageLoader } from "@/components/ui/page";

import BarChart from "./components/bar-chart/bar-chart";
import CapStatsSummary from "./components/cap-stats-summary/cap-stats-summary";
import CockpitTimeline from "./components/cockpit-timeline";
import CognitiveLoad from "./components/cognitive-load/cognitive-load";
import CognitiveLoadBreakdown from "./components/cognitive-load-breakdown/cognitive-load-breakdown";
import CognitiveLoadTimeline from "./components/cognitive-load-timeline/cognitive-load-timeline";
import ColorPhaseScore from "./components/color-phase-score/color-phase-score";
import ColumnEyeTracking from "./components/column-eye-tracking/column-eye-tracking";
import CrewCognitiveLoad from "./components/crew-cognitive-load/crew-cognitive-load";
import CycleContainer from "./components/cycle-container/cycle-container";
import DigitalSignatureExposure from "./components/digital-signature-exposure/digital-signature-exposure";
import AltitudeFlightPathXEyeTrackingCanvas from "./components/dual-components/altitude-flight-path-x-eye-tracking-canvas";
import EyeTrackingCanvasXCognitiveLoad from "./components/dual-components/eye-tracking-canvas-x-cognitive-load";
import FlightPathXCognitiveLoad from "./components/dual-components/flight-path-x-cognitive-load";
import FlightPathXEyeTrackingCanvas from "./components/dual-components/flight-path-x-eye-tracking-canvas";
import MultiFlightPathCanvasXAltitudeGrid from "./components/dual-components/multi-flight-path-canvas-x-altitude-grid";
import EyeTracking from "./components/eye-tracking/eye-tracking";
import EyeTrackingContainer from "./components/eye-tracking-container/eye-tracking-container";
import FlightCircuit from "./components/flight-path/flight-circuit/flight-circuit";
import FlightPath from "./components/flight-path/flight-path";
import FlightPathAndEyeTracking from "./components/flight-path-and-eyetracking/flight-path-and-eyetracking";
import ImpactHeatmap from "./components/impact-heatmap/impact-heatmap";
import { LandingScore } from "./components/landing-score";
import LineProcess from "./components/line-process/line-process";
import MapDisplay from "./components/map-display/map-display";
import MapTimeline from "./components/map-timeline/map-timeline";
import OverallPhaseScore from "./components/overall-phase-score/overall-phase-score";
import { OverallScore } from "./components/overall-score";
import { PerformanceScore } from "./components/performance-score/performance-score";
import PhaseSummary from "./components/phase-summary/phase-summary";
import ProgressScore from "./components/progress-score/progress-score";
import ProgressScoreTable from "./components/progress-score-table/progress-score-table";
import ScoreRadarChart from "./components/radar-chart";
import RecognitionScore from "./components/recognition-score/recognition-score";
import ResponseTime from "./components/response-time/response-time";
import Results from "./components/results/results";
import StatsSummary from "./components/stats-summary/stats-summary";
import StatsSummaryWithDropdown from "./components/stats-summary-with-dropdown/stats-summary-with-dropdown";
import StatsSummaryWithModalReport from "./components/stats-summary-with-modal-report/stats-summary-with-modal-report";
import TimelineChart from "./components/timeline-chart/timeline-chart";
import ComposableChart from "./components/composable-chart/composable-chart";
import CustomRenderer from "./components/custom-renderer/custom-renderer";

import { useSessionInfo } from "./hooks/useSessionInfo";

import { getTimeDifference } from "@/utils/get-time-difference";
import useQueryParams from "@/utils/use-query-params";

import { Main } from "./session-detail.styles";

const localeFn = (target: string) => import(`./locale/${target.toLowerCase()}.json`);

const SessionDetail = () => {
    const { getSessionSubtitleDetails, logoUrl, title, state } = useSessionInfo();
    const { gridLayoutData, dashboardData } = useSessionDetailsContext();
    const params: ReturnType<typeof useQueryParams> = useQueryParams();

    const sessionPeriodFrom = params.get("from");
    const sessionPeriodTo = params.get("to");
    const scenarioInstanceId = params.get("scenarioInstanceId");
    const sessionName = params.get("name");
    const simulationName = params.get("simulationName");

    const sessionPageInput = useMemo((): BuildHeatSessionGlobalInput | null => {
        if (!sessionPeriodFrom && !sessionPeriodTo) {
            return null;
        }

        const sessionDate = moment(sessionPeriodFrom || sessionPeriodTo).format("DD/MM/YYYY");
        const fromTime = sessionPeriodFrom ? moment(sessionPeriodFrom).format("HH:mm") : "";
        const sessionDuration =
            sessionPeriodFrom && sessionPeriodTo
                ? getTimeDifference(new Date(sessionPeriodFrom), new Date(sessionPeriodTo))
                : "";

        return {
            generation: "legacy",
            dimensionId: scenarioInstanceId ?? null,
            title,
            sessionDate,
            fromTime,
            sessionDuration,
            dimension: null,
            layout: gridLayoutData ?? null,
            selectedRealm: null,
            payload: dashboardData ?? null,
            scenarioInstance: {
                scenarioInstanceId: scenarioInstanceId ?? undefined,
                scenarioName: sessionName || simulationName || undefined,
                from: sessionPeriodFrom ?? null,
                to: sessionPeriodTo ?? null,
            },
        };
    }, [
        sessionPeriodFrom,
        sessionPeriodTo,
        scenarioInstanceId,
        sessionName,
        simulationName,
        title,
        gridLayoutData,
        dashboardData,
    ]);

    const getComponentById = (component: string) => {
        const wrap = (node: JSX.Element | null) =>
            node ? <ErrorBoundary name={component}>{node}</ErrorBoundary> : null;

        switch (component) {
            case "LandingScore":
                return wrap(<LandingScore />);
            case "PerformanceScore":
                return wrap(<PerformanceScore />);
            case "MapTimeline":
                return wrap(<MapTimeline />);
            case "OverallScore":
                return wrap(<OverallScore />);
            case "CockpitTimeline":
                return wrap(<CockpitTimeline />);
            case "RadarChart":
                return wrap(<ScoreRadarChart />);

            //Heat Next imported components
            case "CognitiveLoad":
                return wrap(<CognitiveLoad />);
            case "DigitalSignatureExposure":
                return wrap(<DigitalSignatureExposure />);
            case "ColumnEyeTracking":
                return wrap(<ColumnEyeTracking />);
            case "EyeTrackingContainer":
                return wrap(<EyeTrackingContainer />);
            case "EyeTracking":
                return wrap(<EyeTracking />);
            case "EyeTrackingCanvasXCognitiveLoad": //Dual Component
                return wrap(<EyeTrackingCanvasXCognitiveLoad />);
            case "FlightPathXEyeTrackingCanvas": //Dual Component
                return wrap(<FlightPathXEyeTrackingCanvas />);
            case "FlightPathXCognitiveLoad": //Dual Component
                return wrap(<FlightPathXCognitiveLoad />);
            case "AltitudeFlightPathXEyeTrackingCanvas": //Dual Component
                return wrap(<AltitudeFlightPathXEyeTrackingCanvas />);
            case "MultiFlightPathCanvasXAltitudeGrid": //Dual Component
                return wrap(<MultiFlightPathCanvasXAltitudeGrid />);
            case "FlightPath": //FlightPath with playback using dual component
                return wrap(<FlightPath />);
            case "FlightCircuit":
                return wrap(<FlightCircuit />);
            case "FlightPathCanvas":
                return wrap(<FlightPathAndEyeTracking />);
            case "FlightPathAndEyeTracking":
                return wrap(<FlightPathAndEyeTracking />);
            case "MapDisplay": //previously MapTimeline
                return wrap(<MapDisplay />);
            case "Results": //previously QuestionResults
                return wrap(<Results />);
            case "RecognitionScore": //previously RecognitionScoring
                return wrap(<RecognitionScore />);
            case "TimelineChart":
                return wrap(<TimelineChart />);
            case "ComposableChart":
                return wrap(<ComposableChart />);
            case "CustomRenderer":
                return wrap(<CustomRenderer />);
            case "BarChart":
                return wrap(<BarChart />);
            case "ImpactHeatmap":
                return wrap(<ImpactHeatmap />);
            case "CrewCognitiveLoad":
                return wrap(<CrewCognitiveLoad />);
            case "CognitiveLoadBreakdown":
                return wrap(<CognitiveLoadBreakdown />);
            case "CognitiveLoadTimeline":
                return wrap(<CognitiveLoadTimeline />);
            case "ResponseTime": //previously CommunicationTimeline
                return wrap(<ResponseTime />);
            case "ColorPhaseScore":
                return wrap(<ColorPhaseScore />);
            case "OverallPhaseScore":
                return wrap(<OverallPhaseScore />);
            case "CapStatsSummary":
                return wrap(<CapStatsSummary />);
            case "StatsSummary":
                return wrap(<StatsSummary />);
            case "StatsSummaryWithDropdown":
                return wrap(<StatsSummaryWithDropdown />);
            case "StatsSummaryWithModalReport":
                return wrap(<StatsSummaryWithModalReport />);
            case "ProgressScore":
                return wrap(<ProgressScore />);
            case "ProgressScoreTable":
                return wrap(<ProgressScoreTable />);
            case "CycleContainer":
                return wrap(<CycleContainer />);
            case "PhaseSummary":
                return wrap(<PhaseSummary />);
            case "LineProcess":
                return wrap(<LineProcess />);
            default:
                return null;
        }
    };

    return (
        <Page
            pageTitle={false}
            title={title}
            showBackButton={true}
            showDetails={true}
            subtitleDetailText={getSessionSubtitleDetails}
            logoUrl={state?.logoUrl || logoUrl}
        >
            <Main>
                <SessionPageContextProvider input={sessionPageInput}>
                    <LayoutGrid getComponentById={getComponentById} />
                </SessionPageContextProvider>
            </Main>
        </Page>
    );
};

const SessionDetailPage = () => (
    <VRIntlProviderComponent localeFn={localeFn} id="session-detail" fallback={<PageLoader />}>
        <SessionDetail />
    </VRIntlProviderComponent>
);

export default SessionDetailPage;
